import { Badge, colors, Indent, Stack, Typography } from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { FIRST_TAX_YEAR_WITHOUT_174 } from '@neo.tax/rd/constants';
import { useRdContext } from '@neo.tax/rd/context';
import { useUpdateRdProject, RdProject } from '@neo.tax/rd/hooks';
import { evictQres, HIGHLIGHT_VARIANT } from '@neo.tax/shared';

import { useRdProjectEligibilityIds } from './hooks';
import { SectionElectionToggle } from './SectionElectionToggle';

export const IrsSectionElectionToggles = ({ rdProject }: { rdProject: RdProject }): ReactElement => {
  const { consideredForRdToggleId } = useRdProjectEligibilityIds();
  const [updateRdProject] = useUpdateRdProject({ rdProject, includeOptimisticResponse: true });
  const { fiscalYear } = useRdContext();

  const { consideredForRd, isSection174Eligible } = rdProject;
  const isTy2025OrLater = fiscalYear.year >= FIRST_TAX_YEAR_WITHOUT_174;

  return (
    <>
      <SectionElectionToggle
        title="Consider Project for R&D Credit"
        subtitle="Turning on states that the project (1) used a process of experimentation; (2) had technical challenges/uncertainties; (3) was a new/improved product/process"
        variant={HIGHLIGHT_VARIANT}
        checked={consideredForRd}
        id={consideredForRdToggleId}
        onChange={(checked): void => {
          // When turning R&D credit off, also clear §174 so the optimistic response matches the
          // server-side cascade (see rd-projects.service#updateRdProject). Without this the
          // "§174 Only" badge flashes briefly before the real response arrives.
          updateRdProject(
            checked ? { consideredForRd: true } : { consideredForRd: false, isSection174Eligible: false },
            { update: evictQres },
          );
        }}
      />

      {!consideredForRd && !isTy2025OrLater && isSection174Eligible && (
        <Indent border={true}>
          <Stack direction="column" gap={1} paddingY={1} alignItems="start">
            <Badge colorVariant="default" dot={false}>
              §174 Only
            </Badge>
            <Typography fontSize="xs" fontWeight="medium" color={colors.gray[500]}>
              This project was previously marked for Section 174 capitalization, which was required for TY2022–2024
              under the TCJA. Updated tax law no longer requires §174 capitalization for TY2025+, so this setting is
              kept as a read-only historical record.
            </Typography>
          </Stack>
        </Indent>
      )}
    </>
  );
};
